import { enableProdMode } from '@angular/core';
import { provideNativeDateAdapter } from '@angular/material/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { PreloadAllModules, provideRouter, withPreloading, withRouterConfig } from '@angular/router';
import { environment } from '@env/environment';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import { withNgxsStoragePlugin } from '@ngxs/storage-plugin';
import { provideStates, provideStore } from '@ngxs/store';
import { appRoutes } from '@portal-client/app/app.routes';
import { AppRootComponent } from '@portal-client/app/components/app-root/app-root.component';
import { AuthEventHandlerService } from '@portal-client/core/auth/auth-event-handler.service';
import { CoreState } from '@portal-client/core/core-data.service';
import { provideCore } from '@portal-client/core/core.providers';
import { CurrentConfiguratorService } from '@portal-client/core/current/current-configurator.service';
import { LayoutEventHandlerService } from '@portal-client/core/layout/layout-event-handler.service';
import { LoginEventHandlerService } from '@portal-client/core/login/login-event-handler.service';
import { AuthDialogsEffect } from '@portal-core/auth/effects/auth-dialogs.effect';
import { AuthLogoutRedirectEffect } from '@portal-core/auth/effects/auth-logout-redirect.effect';
import { AuthResettablesEffect } from '@portal-core/auth/effects/auth-resettables.effect';
import { provideAuthServices } from '@portal-core/auth/services/auth-services.providers';
import { BranchesState } from '@portal-core/branches/services/branches-data.service';
import { BuildsState } from '@portal-core/builds/services/builds-data.service';
import { BuildsScheduleState } from '@portal-core/builds/services/builds-schedule-data.service';
import { ChecklistTemplatesState } from '@portal-core/checklist-templates/services/checklist-templates-data.service';
import { CommitsState } from '@portal-core/commits/services/commits-data.service';
import { provideCurrentServices } from '@portal-core/current/services/current-services.providers';
import { LanguagesState } from '@portal-core/languages/services/languages-data.service';
import { provideLayoutServices } from '@portal-core/layout/services/layout-services.providers';
import { LicenseHostMapsState } from '@portal-core/license-host-maps/services/license-host-maps-data.service';
import { LicenseHostMapsFrontDoorState } from '@portal-core/license-host-maps/services/license-host-maps-front-door-data.service';
import { LicenseStorageState } from '@portal-core/license-storage/services/license-storage-data.service';
import { LicenseUsersState } from '@portal-core/license-users/services/license-users-data.service';
import { LicensesState } from '@portal-core/licenses/services/licenses-data.service';
import { MergeRequestsState } from '@portal-core/merge-requests/services/merge-requests-data.service';
import { ConversationsState } from '@portal-core/messages/services/conversations-data.service';
import { MessagesState } from '@portal-core/messages/services/messages-data.service';
import { NotificationsState } from '@portal-core/notifications/services/notifications-data.service';
import { OAuthState } from '@portal-core/oauth/services/oauth-data.service';
import { CshIdCallsReportState } from '@portal-core/output-analytics/services/csh-id-calls/csh-id-calls-report-data.service';
import { OutputAnalyticsKeyState } from '@portal-core/output-analytics/services/output-analytics-key/output-analytics-key-data.service';
import { OutputAnalyticsReportItemState } from '@portal-core/output-analytics/services/report-items/output-analytics-report-item-data.service';
import { SearchQueryReportState } from '@portal-core/output-analytics/services/search-query/search-query-report-data.service';
import { TopicViewsReportState } from '@portal-core/output-analytics/services/topic-views/topic-views-report-data.service';
import { CurrentUserPermissionsState } from '@portal-core/permissions/services/current-user-permissions-data.service';
import { LogStreamRowState } from '@portal-core/processes/services/log-stream-row-data.service';
import { ProjectChecklistItemsState } from '@portal-core/project-checklists/services/project-checklist-items/project-checklist-items-data.service';
import { ProjectChecklistsState } from '@portal-core/project-checklists/services/project-checklists/project-checklists-data.service';
import { ProjectInfoState } from '@portal-core/project-info/services/project-info-data.service';
import { ProjectsState } from '@portal-core/projects/services/projects-data.service';
import { PublicationsState } from '@portal-core/publications/services/publications-data.service';
import { ProjectReportStatisticsState } from '@portal-core/reports/services/project-report-statistics-data.service';
import { ProjectScanStatisticsState } from '@portal-core/reports/services/project-scan-statistics-data.service';
import { ProjectReportBrokenLinksState } from '@portal-core/reports/services/project-reports-broken-links-data.service';
import { ProjectReportsUndefinedConditionTagsDataState } from '@portal-core/reports/services/project-reports-undefined-condition-tags-data.service';
import { ProjectReportsUndefinedFileTagsState } from '@portal-core/reports/services/project-reports-undefined-file-tags-data.service';
import { ProjectReportsUndefinedGlossaryTermsState } from '@portal-core/reports/services/project-reports-undefined-glossary-terms-data.service';
import { ProjectReportsUndefinedVariablesState } from '@portal-core/reports/services/project-reports-undefined-variables-data.service';
import { ReviewFileUsersState } from '@portal-core/reviews/review-file-users/services/review-file-users-data.service';
import { ReviewFilesState } from '@portal-core/reviews/review-files/services/review-files-data.service';
import { ReviewPackageUsersState } from '@portal-core/reviews/review-package-users/services/review-package-users-data.service';
import { ReviewPackagesState } from '@portal-core/reviews/review-packages/services/review-packages-data.service';
import { ContentSecurityPolicyState } from '@portal-core/sites/services/content-security-policy/content-security-policy-data.service';
import { RobotsExclusionState } from '@portal-core/sites/services/robots-exclusion/robots-exclusion-data.service';
import { SiteStylesState } from '@portal-core/sites/services/site-styles/site-styles-data.service';
import { SiteUrlsState } from '@portal-core/sites/services/site-urls/site-urls-data.service';
import { SitesState } from '@portal-core/sites/services/sites-data.service';
import { WebhookChannelsState } from '@portal-core/slack/services/slack-webhooks-channels-data.service';
import { WebhookSubscribersState } from '@portal-core/slack/services/slack-webhooks-subscribers-data.service';
import { SystemMessagesState } from '@portal-core/system-messages/services/system-messages-data.service';
import { TargetsState } from '@portal-core/targets/services/targets-data.service';
import { TaskAnalyticsState } from '@portal-core/task-analytics/services/task-analytics-data.service';
import { TaskBoardsState } from '@portal-core/task-boards/services/task-boards-data.service';
import { TaskAssetsState } from '@portal-core/tasks/services/task-assets-data.service';
import { TaskCommentsState } from '@portal-core/tasks/services/task-comments-data.service';
import { TasksState } from '@portal-core/tasks/services/tasks-data.service';
import { TeamsState } from '@portal-core/teams/services/teams-data.service';
import { TranslationPackageLanguageBranchesState } from '@portal-core/translation-package-language-branches/services/translation-package-language-branches-data.service';
import { TranslationPackagesState } from '@portal-core/translation-packages/services/translation-packages-data.service';
import { renameGridColumnNames } from '@portal-core/ui/grid/migrations/rename.grid.column.names.migration';
import { addIdAddTypeToPageFilterGroup } from '@portal-core/ui/page-filters/migrations/add.id.add.type.to.page-filter-group.migration';
import { UsersState } from '@portal-core/users/services/users-data.service';
import { WorkspaceFilesState } from '@portal-core/workspaces/services/workspace-files/workspace-files-data.service';

if (environment.production) {
  enableProdMode();
}

// Migrate legacy hash-based URLs (`/#/path`) to the new path-based form so old bookmarks and previously-sent
// email/confirm links keep working after the switch off hash routing. The server serves index.html for the
// app root, so a `/#/…` URL still loads the app; here we rewrite the location to the path form BEFORE
// bootstrapApplication so the router's initial navigation sees the correct URL. The hash already carries the
// route path plus any route query (e.g. `#/1/reviews?tab=x`). We prepend the app's live base-href prefix so
// the path the app is served under is preserved — that prefix is `/` at the domain root (dev, and prod/stage)
// but is rewritten to e.g. `/client/` on deployments served under an ingress path prefix (see the Helm
// rewrite-baseHref sub_filter). Reading the actual <base href> adapts to either; dropping the prefix would
// 404. Uses replaceState so no extra history entry is created.
const legacyHash = window.location.hash;
if (legacyHash.startsWith('#/')) {
  const baseHref = document.querySelector('base')?.getAttribute('href') ?? '/';
  const basePath = baseHref.replace(/\/$/, ''); // '' when served at root (prod, stage), e.g. '/client' under an ingress path prefix
  history.replaceState(history.state, '', basePath + legacyHash.substring(1));
}

bootstrapApplication(AppRootComponent, {
  providers: [
    // App-wide root providers (was CoreModule, consumed via importProvidersFrom).
    ...provideCore(),
    // Router. Path-based location (no hash) — a legacy `/#/…` bookmark is rewritten to the path form in the
    // pre-bootstrap shim above. (Was hash-based via withHashLocation(); dropped when migrating off `#` routing.)
    provideRouter(appRoutes, withPreloading(PreloadAllModules), withRouterConfig({ paramsInheritanceStrategy: 'always' })),
    // Provides the Angular Material native DateAdapter for MatDatepicker app-wide.
    provideNativeDateAdapter(),
    // NGXS store + plugins (was NgxsModule.forRoot([CoreState]) + the storage/devtools plugin modules in CoreModule).
    provideStore(
      [CoreState],
      // NgxsStoragePlugin must be the first NGXS plugin.
      withNgxsStoragePlugin({
        keys: [
          'coreState.settings',
          'oAuthState',
          'projectReportsPageState.filters.chartSize',
          'Branches.grids.project-branches-page.config',
          'Builds.grids.project-builds-page.config',
          'BuildsSchedule.grids.project-schedule-builds-page.config',
          'Publications.grids.project-publications-page.config',
          'LicenseUsers.grids.users-page.config',
          'Projects.grids.projects-page.config',
          'ReviewFiles.grids.reviews-page.config',
          'ReviewFiles.pagedLists.reviews-page.config',
          'ReviewPackages.grids.reviews-page.config',
          'ReviewPackages.pagedLists.reviews-page.config',
          'ReviewsPageState.filters.userReviewState',
          'Teams.grids.teams-page.config',
          'Sites.grids.sites-page.config',
          'SiteUrls.grids.site-urls-page.config',
          'OutputAnalyticsKeys.grids.output-analytics-keys-page.config',
          'CshIdCallsReports.grids.csh-id-call-reports.config',
          'SearchQueryReports.grids.search-phrase-reports.config',
          'SearchQueryReports.grids.search-phrases-no-result-reports.config',
          'TopicViewsReports.grids.topic-view-reports.config',
          'OutputAnalyticsReportItems.grids.browser-stats-reports.config',
          'OutputAnalyticsReportItems.grids.os-stats-reports.config',
          'Tasks.grids.tasks-grid-archive-page.config',
          'Tasks.grids.tasks-grid-backlog-page.config',
          'Tasks.grids.tasks-grid-all-page.config',
          'TranslationPackageLanguageBranches.grids.project-translation-branches-page.config',
          'TranslationPackages.grids.project-translations-page.config',
          'WorkspaceFiles.grids.workspace-overview.config'
        ],
        // Run any migrations or clean up
        afterDeserialize: (state, key) => {
          state = addIdAddTypeToPageFilterGroup(state, key);
          return renameGridColumnNames(state, key);
        }
      }),
      // NgxsReduxDevtoolsPlugin must be the last NGXS plugin.
      withNgxsReduxDevtoolsPlugin({
        disabled: environment.production
      })
    ),
    provideStates([
      BranchesState,
      BuildsScheduleState,
      BuildsState,
      ChecklistTemplatesState,
      CommitsState,
      ContentSecurityPolicyState,
      ConversationsState,
      CshIdCallsReportState,
      CurrentUserPermissionsState,
      LanguagesState,
      LicenseHostMapsFrontDoorState,
      LicenseHostMapsState,
      LicenseStorageState,
      LicenseUsersState,
      LicensesState,
      LogStreamRowState,
      MergeRequestsState,
      MessagesState,
      NotificationsState,
      OAuthState,
      OutputAnalyticsKeyState,
      OutputAnalyticsReportItemState,
      ProjectChecklistItemsState,
      ProjectChecklistsState,
      ProjectInfoState,
      ProjectReportBrokenLinksState,
      ProjectReportStatisticsState,
      ProjectReportsUndefinedConditionTagsDataState,
      ProjectReportsUndefinedFileTagsState,
      ProjectReportsUndefinedGlossaryTermsState,
      ProjectReportsUndefinedVariablesState,
      ProjectScanStatisticsState,
      ProjectsState,
      PublicationsState,
      ReviewFileUsersState,
      ReviewFilesState,
      ReviewPackageUsersState,
      ReviewPackagesState,
      RobotsExclusionState,
      SearchQueryReportState,
      SiteStylesState,
      SiteUrlsState,
      SitesState,
      SystemMessagesState,
      TargetsState,
      TaskAnalyticsState,
      TaskAssetsState,
      TaskBoardsState,
      TaskCommentsState,
      TasksState,
      TeamsState,
      TopicViewsReportState,
      TranslationPackageLanguageBranchesState,
      TranslationPackagesState,
      UsersState,
      WebhookChannelsState,
      WebhookSubscribersState,
      WorkspaceFilesState,
    ]),
    provideAuthServices({
      onAuthError: [AuthEventHandlerService],
      onAuthRoute: AuthEventHandlerService,
      onLoginComplete: LoginEventHandlerService,
      effects: [AuthResettablesEffect, AuthDialogsEffect, AuthLogoutRedirectEffect]
    }),
    provideCurrentServices({
      getLicenseUser: CurrentConfiguratorService,
      getLicenseUser$: CurrentConfiguratorService,
      getLicense: CurrentConfiguratorService,
      getLicense$: CurrentConfiguratorService,
      getActiveLicense: CurrentConfiguratorService,
      getActiveLicense$: CurrentConfiguratorService,
      isSysAdmin: CurrentConfiguratorService
    }),
    provideLayoutServices({
      onOpenAboutDialog: [LayoutEventHandlerService]
    })
  ]
})
  .catch(err => console.log(err));
